import { ChangeDetectionStrategy, Component, input } from '@angular/core';

import { ChatMessage, ChatPart } from '@core/tellent-assistant-types';
import { IconButtonComponent } from '@core/user-interface-legacy';

import { TellentAssistantBrandMarkComponent } from '../brand-mark/tellent-assistant-brand-mark.component';
import { TellentAssistantTextPartComponent } from '../parts/text-part/text-part.component';
import { TellentAssistantToolCallPartComponent } from '../parts/tool-call-part/tool-call-part.component';
import { TellentAssistantToolResultPartComponent } from '../parts/tool-result-part/tool-result-part.component';

@Component({
  selector: 'tellent-assistant-message',
  standalone: true,
  imports: [
    IconButtonComponent,
    TellentAssistantBrandMarkComponent,
    TellentAssistantTextPartComponent,
    TellentAssistantToolCallPartComponent,
    TellentAssistantToolResultPartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <article [class]="'role-' + message().role">
      @if (message().role === 'assistant') {
        <tellent-assistant-brand-mark class="avatar" />
      }
      <div class="body">
        @for (p of message().parts; track partKey(p)) {
          @switch (p.kind) {
            @case ('text') {
              <tellent-assistant-text-part [content]="p.content" [done]="p.done" />
            }
            @case ('tool-call') {
              <tellent-assistant-tool-call-part
                [name]="p.name"
                [status]="p.status"
                [durationMs]="p.durationMs ?? null"
              />
            }
            @case ('tool-result') {
              <tellent-assistant-tool-result-part [part]="p" />
            }
          }
        }
      </div>
      @if (message().role === 'assistant') {
        <rt-icon-button
          iconName="content-copy"
          size="small"
          variant="plain"
          class="copy"
          (click)="copy()"
        />
      }
    </article>
  `,
  styleUrls: ['./tellent-assistant-message.component.less'],
})
export class TellentAssistantMessageComponent {
  public readonly message = input.required<ChatMessage>();

  protected partKey(p: ChatPart): string {
    switch (p.kind) {
      case 'text':
        return `text-${p.messageId}`;
      case 'tool-call':
      case 'tool-result':
        return `${p.kind}-${p.toolCallId}`;
      default:
        return p.kind;
    }
  }

  protected async copy(): Promise<void> {
    const text = this.message()
      .parts.filter(p => p.kind === 'text')
      .map(p => (<Extract<ChatPart, { kind: 'text' }>>p).content)
      .join('\n');
    await navigator.clipboard.writeText(text);
  }
}
